import { BillingManager } from '@/billing/BillingManager';
import { GOOGLE_PLAY_VERIFIED_PRODUCT_ID } from '@/config/subscriptions';
import { baseSupabase } from '@/integrations/supabase/client';
import { invokeAuthedEdge, InvokeNotAuthenticatedError } from '@/lib/supabase/invokeAuthedEdge';

export type PurchaseState = 'idle' | 'pending' | 'active' | 'failed';

export interface PurchaseStateSnapshot {
  state: PurchaseState;
  productId: string | null;
  purchaseToken: string | null;
  lastUpdatedAt: string;
  failureReason: string | null;
}

export interface EntitlementSnapshot {
  state: PurchaseState;
  source: 'db';
  expiresAt: string | null;
  checkedAt: string;
}

interface ValidateReceiptInput {
  platform: 'android' | 'ios';
  purchaseToken?: string;
  productId?: string;
  packageName?: string;
  jwsRepresentation?: string;
  transactionId?: string;
  isRestore?: boolean;
}

interface ValidatePurchaseResponse {
  verified: boolean;
  entitlementState: PurchaseState;
  expiresAt?: string | null;
  requestId?: string;
  error?: string;
}

const STORAGE_KEY = 'blissnet.billing.purchase_state.v1';
const PACKAGE_NAME = 'com.blissnet.app';
const ACCESS_GRANTING_STATUSES = new Set(['active', 'trialing', 'grace_period', 'canceled']);
const ENTITLEMENT_CACHE_TTL_MS = 5 * 60 * 1000; // P2: align with EntitlementService / flagService

function nowIso(): string {
  return new Date().toISOString();
}

function getDefaultSnapshot(): PurchaseStateSnapshot {
  return {
    state: 'idle',
    productId: null,
    purchaseToken: null,
    lastUpdatedAt: nowIso(),
    failureReason: null,
  };
}

function parseSnapshot(raw: string | null): PurchaseStateSnapshot {
  if (!raw) return getDefaultSnapshot();
  try {
    const parsed = JSON.parse(raw) as Partial<PurchaseStateSnapshot>;
    const state = parsed.state;
    if (state !== 'idle' && state !== 'pending' && state !== 'active' && state !== 'failed') {
      return getDefaultSnapshot();
    }
    return {
      state,
      productId: parsed.productId ?? null,
      purchaseToken: parsed.purchaseToken ?? null,
      lastUpdatedAt: parsed.lastUpdatedAt ?? nowIso(),
      failureReason: parsed.failureReason ?? null,
    };
  } catch {
    return getDefaultSnapshot();
  }
}

class BillingService {
  private validateInFlight = new Map<string, Promise<ValidatePurchaseResponse>>();
  private entitlementCache: { userId: string; snapshot: EntitlementSnapshot } | null = null;
  private lastEntitlementCheckAt = 0;

  getPurchaseState(): PurchaseStateSnapshot {
    return parseSnapshot(localStorage.getItem(STORAGE_KEY));
  }

  private setPurchaseState(next: PurchaseStateSnapshot): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  private updateState(
    state: PurchaseState,
    patch?: Partial<Pick<PurchaseStateSnapshot, 'productId' | 'purchaseToken' | 'failureReason'>>
  ): PurchaseStateSnapshot {
    const current = this.getPurchaseState();
    const next: PurchaseStateSnapshot = {
      state,
      productId: patch?.productId ?? current.productId,
      purchaseToken: patch?.purchaseToken ?? current.purchaseToken,
      failureReason: patch?.failureReason ?? null,
      lastUpdatedAt: nowIso(),
    };
    this.setPurchaseState(next);
    return next;
  }

  async purchase(): Promise<PurchaseStateSnapshot> {
    this.updateState('pending', {
      productId: GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
      purchaseToken: null,
      failureReason: null,
    });

    try {
      const billing = BillingManager.getInstance();
      await billing.initialize();
      const result = await billing.purchaseVerified();

      if (!result.success || !result.purchaseToken) {
        return this.updateState('failed', {
          failureReason: result.error?.message ?? 'purchase_failed',
        });
      }

      this.updateState('pending', {
        purchaseToken: result.purchaseToken,
        failureReason: null,
      });

      const validation = await this.validateReceipt({
        platform: 'android',
        purchaseToken: result.purchaseToken,
        productId: result.productId ?? GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
        packageName: PACKAGE_NAME,
        isRestore: false,
      });

      if (!validation.verified || validation.entitlementState !== 'active') {
        return this.updateState('failed', {
          failureReason: validation.error ?? 'receipt_validation_failed',
        });
      }

      const entitlement = await this.getEntitlement(true);
      if (entitlement.state !== 'active') {
        return this.updateState('failed', {
          failureReason: 'entitlement_not_active_after_validation',
        });
      }

      return this.updateState('active', { failureReason: null });
    } catch (error) {
      if (error instanceof InvokeNotAuthenticatedError) {
        return this.updateState('failed', { failureReason: 'not_authenticated' });
      }
      const reason = error instanceof Error ? error.message : 'purchase_exception';
      return this.updateState('failed', { failureReason: reason });
    }
  }

  async restorePurchases(): Promise<PurchaseStateSnapshot> {
    this.updateState('pending', {
      productId: GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
      failureReason: null,
    });

    try {
      const billing = BillingManager.getInstance();
      await billing.initialize();
      const restored = await billing.restorePurchases();

      if (restored.length === 0) {
        const entitlement = await this.getEntitlement(true);
        if (entitlement.state === 'active') {
          return this.updateState('active', { failureReason: null });
        }
        return this.updateState('failed', { failureReason: 'no_restorable_purchase' });
      }

      let validatedAny = false;
      for (const purchase of restored) {
        if (!purchase.purchaseToken) continue;
        this.updateState('pending', {
          purchaseToken: purchase.purchaseToken,
          productId: purchase.productId ?? GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
        });
        const result = await this.validateReceipt({
          platform: 'android',
          purchaseToken: purchase.purchaseToken,
          productId: purchase.productId ?? GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
          packageName: PACKAGE_NAME,
          isRestore: true,
        });
        if (result.verified && result.entitlementState === 'active') {
          validatedAny = true;
        }
      }

      const entitlement = await this.getEntitlement(true);
      if (validatedAny && entitlement.state === 'active') {
        return this.updateState('active', { failureReason: null });
      }

      return this.updateState('failed', {
        failureReason: 'restore_validation_failed',
      });
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'restore_exception';
      return this.updateState('failed', { failureReason: reason });
    }
  }

  async validateReceipt(input: ValidateReceiptInput): Promise<ValidatePurchaseResponse> {
    const idempotencyKey = [
      input.platform,
      input.purchaseToken ?? '',
      input.transactionId ?? '',
      input.productId ?? GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
      input.isRestore ? 'restore' : 'purchase',
    ].join(':');

    const existing = this.validateInFlight.get(idempotencyKey);
    if (existing) return existing;

    const validationPromise = (async (): Promise<ValidatePurchaseResponse> => {
      const payload: ValidateReceiptInput = {
        ...input,
        productId: input.productId ?? GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
        packageName: input.packageName ?? PACKAGE_NAME,
      };

      const response = await invokeAuthedEdge<ValidatePurchaseResponse>('validate_purchase', payload, {
        route: 'billing_service_validate_receipt',
      });

      if (response.error) {
        return {
          verified: false,
          entitlementState: 'failed',
          requestId: response.error.requestId,
          error: response.error.message,
        };
      }

      if (!response.data) {
        return {
          verified: false,
          entitlementState: 'failed',
          error: 'empty_validation_response',
        };
      }

      return response.data;
    })();

    this.validateInFlight.set(idempotencyKey, validationPromise);
    try {
      return await validationPromise;
    } finally {
      this.validateInFlight.delete(idempotencyKey);
    }
  }

  async getEntitlement(forceRefresh = false): Promise<EntitlementSnapshot> {
    const { data: authData } = await baseSupabase.auth.getUser();
    const userId = authData.user?.id;
    if (!userId) {
      return { state: 'failed', source: 'db', expiresAt: null, checkedAt: nowIso() };
    }

    if (
      !forceRefresh &&
      this.entitlementCache?.userId === userId &&
      Date.now() - this.lastEntitlementCheckAt < ENTITLEMENT_CACHE_TTL_MS
    ) {
      return this.entitlementCache.snapshot;
    }

    const query = baseSupabase
      .from('user_subscriptions')
      .select('status,current_period_end,product_code')
      .eq('user_id', userId)
      .eq('product_code', GOOGLE_PLAY_VERIFIED_PRODUCT_ID)
      .order('updated_at', { ascending: false })
      .limit(5);

    const { data, error } = await query;
    if (error || !data || data.length === 0) {
      const snapshot: EntitlementSnapshot = {
        state: 'idle',
        source: 'db',
        expiresAt: null,
        checkedAt: nowIso(),
      };
      this.entitlementCache = { userId, snapshot };
      this.lastEntitlementCheckAt = Date.now();
      return snapshot;
    }

    const nowMs = Date.now();
    for (const row of data) {
      const expiresAt = row.current_period_end ?? null;
      const expiryMs = expiresAt ? Date.parse(expiresAt) : NaN;
      const notExpired = Number.isFinite(expiryMs) ? expiryMs > nowMs : false;
      const hasAccess = notExpired && ACCESS_GRANTING_STATUSES.has(String(row.status));
      if (hasAccess) {
        const snapshot: EntitlementSnapshot = {
          state: 'active',
          source: 'db',
          expiresAt,
          checkedAt: nowIso(),
        };
        if (forceRefresh) {
          this.updateState('active', { failureReason: null });
        }
        this.entitlementCache = { userId, snapshot };
        this.lastEntitlementCheckAt = Date.now();
        return snapshot;
      }
    }

    const idleSnapshot: EntitlementSnapshot = {
      state: 'idle',
      source: 'db',
      expiresAt: null,
      checkedAt: nowIso(),
    };
    this.entitlementCache = { userId, snapshot: idleSnapshot };
    this.lastEntitlementCheckAt = Date.now();
    return idleSnapshot;
  }

  async recoverPendingState(): Promise<PurchaseStateSnapshot> {
    const current = this.getPurchaseState();
    if (current.state !== 'pending') return current;

    const entitlement = await this.getEntitlement(true);
    if (entitlement.state === 'active') {
      return this.updateState('active', { failureReason: null });
    }

    if (current.purchaseToken) {
      const validation = await this.validateReceipt({
        platform: 'android',
        purchaseToken: current.purchaseToken,
        productId: current.productId ?? GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
        packageName: PACKAGE_NAME,
        isRestore: false,
      });

      if (validation.verified && validation.entitlementState === 'active') {
        const refreshed = await this.getEntitlement(true);
        if (refreshed.state === 'active') {
          return this.updateState('active', { failureReason: null });
        }
      }
    }

    return this.updateState('failed', { failureReason: 'pending_recovery_failed' });
  }
}

export const billingService = new BillingService();
